// apps/web/src/pages/dashboard/wallet/PurchaseSuccessPage.tsx
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { trpc } from '@/trpc/client';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { CheckCircle, XCircle, Clock, Loader2, AlertTriangle, ArrowRight } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';

const PurchaseSuccessPage = () => {
    const [searchParams] = useSearchParams();
    const txRef = searchParams.get('tx_ref');
    const urlStatus = searchParams.get('status');
    const hasVerified = useRef(false);

    const [verificationState, setVerificationState] = useState<'verifying' | 'success' | 'failed' | 'pending' | 'mobile_return'>('verifying');
    const [message, setMessage] = useState<string>('Verifying transaction...');
    const [retryCount, setRetryCount] = useState(0);
    const MAX_RETRIES = 10;
    const VERIFICATION_TIMEOUT = 30000; // 30 seconds
    const { user } = useAuthStore();
    const location = useLocation();

    // Resolve txRef from URL params (Deep Link) OR Navigation State (App Resume)
    const effectiveTxRef = txRef || location.state?.txRef;

    const verifyMutation = trpc.wallet.verifyTransaction.useMutation({
        onSuccess: (data) => {
            if (data.status === 'success') {
                setVerificationState('success');
                setMessage(data.message || 'Payment verified successfully.');
            } else if (data.status === 'pending') {
                // If pending, retry instead of stopping immediately
                if (retryCount < MAX_RETRIES) {
                    setRetryCount(prev => prev + 1);
                    setMessage(`Payment processing... checking again (${retryCount + 1}/${MAX_RETRIES})`);
                    setTimeout(() => {
                        if (effectiveTxRef) verifyMutation.mutate({ txRef: effectiveTxRef });
                    }, 2000);
                } else {
                    setVerificationState('pending');
                    setMessage(data.message || 'Payment is taking longer than expected. Please check your wallet shortly.');
                }
            } else {
                setVerificationState('failed');
                setMessage(data.message || 'Payment verification failed.');
            }
        },
        onError: (error) => {
            // Retry logic
            if (retryCount < MAX_RETRIES) {
                setRetryCount(prev => prev + 1);
                setMessage(`Retrying verification... (${retryCount + 1}/${MAX_RETRIES})`);
                setTimeout(() => {
                    if (effectiveTxRef) {
                        verifyMutation.mutate({ txRef: effectiveTxRef });
                    }
                }, 2000); // Wait 2 seconds before retry
            } else {
                setVerificationState('failed');
                setMessage(error.message || 'Could not verify payment status. Please check your wallet or contact support.');
            }
        }
    });

    useEffect(() => {
        // Clear query params or state after successful extraction? Maybe not needed.

        if (!effectiveTxRef) {
            setVerificationState('failed');
            setMessage('Transaction reference missing.');
            return;
        }

        // --- AUTH CHECK FOR MOBILE RETURN ---
        // If user is not logged in, we assume they are on mobile (System Browser)
        // and session is not shared. The App Link failed to intercept, so we are here.
        // We must provide a MANUAL button to jump back to the app using Custom Scheme.
        if (!user) {
            setVerificationState('mobile_return');
            setMessage('Transaction completed. Tap below to return to the app and verify your balance.');
            return;
        }

        // Prevent double-firing in strict mode
        if (hasVerified.current) return;
        hasVerified.current = true;

        // Start verification
        verifyMutation.mutate({ txRef: effectiveTxRef });

        // Set timeout to prevent infinite loading
        const timeout = setTimeout(() => {
            if (verificationState === 'verifying') {
                setVerificationState('pending');
                setMessage('Verification is taking longer than expected. Your payment may still be processing. Please check your wallet in a few minutes.');
            }
        }, VERIFICATION_TIMEOUT);

        return () => clearTimeout(timeout);
    }, [effectiveTxRef, user]);

    let icon;
    let title;
    let description;
    let buttonText = "Return to Wallet";
    let iconClass = "w-12 h-12";

    switch (verificationState) {
        case 'verifying':
            icon = <Loader2 className={`${iconClass} text-blue-500 animate-spin`} />;
            title = "Verifying Payment...";
            description = "Please wait while we confirm your transaction with PayChangu.";
            break;
        case 'success':
            icon = <CheckCircle className={`${iconClass} text-green-500`} />;
            title = "Purchase Successful!";
            description = message;
            buttonText = "Go to Wallet";
            break;
        case 'failed':
            icon = <XCircle className={`${iconClass} text-red-500`} />;
            title = "Verification Failed";
            description = message;
            buttonText = "Try Again / Contact Support";
            break;
        case 'pending':
            icon = <Clock className={`${iconClass} text-yellow-500`} />;
            title = "Purchase Pending";
            description = message;
            break;
        case 'mobile_return':
            icon = <CheckCircle className={`${iconClass} text-green-500`} />;
            title = "Payment Completed";
            description = message;
            buttonText = "Return to App";
            break;
    }

    // Fallback: If no tx_ref and just status (shouldn't happen with new logic but legacy support)
    if (!txRef && urlStatus) {
        // ... existing fallback logic if needed, but the useEffect handles generic missing txRef now.
    }

    return (
        <div className="container py-24 text-center">
            <Card className="max-w-md mx-auto glass">
                <CardHeader>
                    <CardTitle className="flex justify-center mb-4">{icon}</CardTitle>
                    <CardTitle>{title}</CardTitle>
                    <CardDescription>{description}</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-col gap-2">
                        {verificationState === 'verifying' ? (
                            <Button disabled variant="outline">Verifying...</Button>
                        ) : verificationState === 'mobile_return' ? (
                            <Button asChild variant="premium" className="w-full">
                                <a href={`kclogistics://payment-success${window.location.search}`}>Return to App to Verify</a>
                            </Button>
                        ) : (
                            <Button asChild variant={verificationState === 'success' ? 'default' : 'outline'}>
                                <Link to="/dashboard/wallet">{buttonText}</Link>
                            </Button>
                        )}

                        {verificationState === 'failed' && (
                            <p className="text-xs text-muted-foreground mt-2">
                                Ref: {txRef || 'N/A'}
                            </p>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default PurchaseSuccessPage;